/**
 * Hook: useFilterAutoSearch (widget)
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R27
 *
 * Re-runs the search (server-side filtering) whenever the user changes a refine
 * selection on a widget search surface, once an initial search has been
 * performed. Ported from the web app; the `hasSearched` gate prevents firing on
 * the initial render, and selections are debounced so rapid multi-select
 * coalesces into one request instead of firing per change.
 */

import { useEffect, useRef } from 'react';
import type { SearchFilters } from './search-filter-types';

export function useFilterAutoSearch(
  filters: SearchFilters,
  hasSearched: boolean,
  runSearch: () => void,
  debounceMs = 400,
): void {
  // Keep the latest runSearch/hasSearched without making them effect deps, so
  // the effect fires only on a real `filters` change (not on every render).
  const runRef = useRef(runSearch);
  runRef.current = runSearch;
  const hasSearchedRef = useRef(hasSearched);
  hasSearchedRef.current = hasSearched;

  useEffect(() => {
    if (!hasSearchedRef.current) return;
    const timer = setTimeout(() => runRef.current(), debounceMs);
    return () => clearTimeout(timer);
  }, [filters, debounceMs]);
}
